const isPlainObject = (valueArg: object): boolean => {
  const prototype = Object.getPrototypeOf(valueArg);
  return prototype === Object.prototype || prototype === null;
};

/**
 * Compares selected state without serializing it.
 *
 * State updates conventionally preserve references for unchanged branches, so
 * the identity fast path handles the common case. Arrays and plain objects keep
 * structural comparison semantics, including cyclic graphs. Runtime class
 * instances are intentionally compared by reference: traversing their internals
 * can be expensive and may reach sockets, observables, or other cyclic resources
 * that are not state data.
 */
export const isStateSelectionEqual = <T>(
  leftArg: T,
  rightArg: T,
): boolean => {
  const leftToRight = new WeakMap<object, object>();
  const rightToLeft = new WeakMap<object, object>();

  const compare = (left: unknown, right: unknown): boolean => {
    if (Object.is(left, right)) {
      return true;
    }
    if (
      left === null
      || right === null
      || typeof left !== 'object'
      || typeof right !== 'object'
    ) {
      return false;
    }

    if (left instanceof Date || right instanceof Date) {
      return (
        left instanceof Date
        && right instanceof Date
        && Object.is(left.getTime(), right.getTime())
      );
    }

    const leftIsArray = Array.isArray(left);
    const rightIsArray = Array.isArray(right);
    if (leftIsArray !== rightIsArray) {
      return false;
    }
    if (!leftIsArray && (!isPlainObject(left) || !isPlainObject(right))) {
      return false;
    }

    const mappedRight = leftToRight.get(left);
    if (mappedRight) {
      return mappedRight === right;
    }
    const mappedLeft = rightToLeft.get(right);
    if (mappedLeft) {
      return mappedLeft === left;
    }
    leftToRight.set(left, right);
    rightToLeft.set(right, left);

    if (leftIsArray && rightIsArray) {
      if (left.length !== right.length) {
        return false;
      }
      for (let index = 0; index < left.length; index++) {
        if (!compare(left[index], right[index])) {
          return false;
        }
      }
      return true;
    }

    const leftRecord = left as Record<string, unknown>;
    const rightRecord = right as Record<string, unknown>;
    const leftKeys = Object.keys(leftRecord);
    const rightKeys = Object.keys(rightRecord);
    if (leftKeys.length !== rightKeys.length) {
      return false;
    }
    for (const key of leftKeys) {
      if (
        !Object.prototype.hasOwnProperty.call(rightRecord, key)
        || !compare(leftRecord[key], rightRecord[key])
      ) {
        return false;
      }
    }
    return true;
  };

  return compare(leftArg, rightArg);
};
